import React, { useEffect, useMemo, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Staff, StaffSearchRequest } from "../@types";
import { useGetAllStaffMutation } from "../staffApiSlice";

export interface StaffOption {
  id: string;
  name: string;
  email: string;
}

interface StaffDropdownProps {
  value?: string;
  onChange?: (staff: StaffOption | null) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  uiError?: string;
}

const StaffDropdown: React.FC<StaffDropdownProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Select a staff",
  name = "staff",
  id = "staff",
  showLabel = true,
  uiError,
}) => {
  const [getAllStaff, { isLoading, error, isError }] = useGetAllStaffMutation();
  const [staffList, setStaffList] = useState<Staff[]>([]);

  // Fetch staff on mount
  useEffect(() => {
    const fetchStaff = async () => {
      const requestBody: StaffSearchRequest = {
        pageNumber: 0,
        pageSize: 1000, // Fetch a large number to get all staff
        keyword: undefined,
      };

      try {
        const result = await getAllStaff(requestBody).unwrap();
        if (result?.data?.data) {
          setStaffList(result.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch staff:", err);
      }
    };

    fetchStaff();
  }, [getAllStaff]);

  const staffOptions: StaffOption[] = useMemo(() => {
    if (isLoading) {
      return [];
    }

    if (isError) {
      return [];
    }

    return staffList.map((staff: Staff) => ({
      id: staff.id,
      name: staff.fullName,
      email: staff.email,
    }));
  }, [staffList, isLoading, isError]);

  // Helper functions for MySelect
  const getValue = (option: StaffOption) => option.id;
  const getName = (option: StaffOption) => `${option.name} (${option.email})`;

  const handleValueChange = (option: StaffOption) => {
    onChange?.(option);
  };

  return (
    <div className={cn("w-full")}>
      <MySelect<StaffOption>
        name={name}
        id={id}
        label={showLabel ? "Staff" : ""}
        options={staffOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={(id) => {
          const option = staffOptions.find((opt) => opt.id === id);
          onChange?.(option || null);
        }}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
      />

      {isError && (
        <p className="mt-1 text-sm text-primary" role="alert">
          {getErrorDetails(error).message || "Error loading available staff"}
        </p>
      )}

      {uiError && (
        <p className="mt-1 text-sm text-primary" role="alert">
          {uiError}
        </p>
      )}
    </div>
  );
};

export default StaffDropdown;
